import React, { useEffect, useState } from "react";
import styles from "./Home.module.css";
import Button from "../../../components/common/Button";
import Modal from "../../../components/common/Modal/Modal";
import api from "../../../api/axios";
import { toast } from "react-hot-toast";

import { Link } from "react-router-dom";
import {
  Dumbbell,
  Trees,
  Waves,
  Building,
  Car,
  ShieldCheck,
  Gamepad2,
  PartyPopper,
  ChevronRight,
  ChevronLeft,
  Users,
  MapPin,
  Clock,
  Phone,
  ShieldAlert,
  Truck,
  Flame,
  ArrowRight,
  Sparkles,
  Camera,
  Heart,
  Siren,
  CalendarDays,
  Bell,
  Megaphone,
  TrendingUp,
  Star,
  Briefcase
} from "lucide-react";

const getBusinessLink = (biz) => {
  let link = biz.website || biz.instagram || biz.facebook;
  if (!link) return null;
  if (!link.startsWith('http://') && !link.startsWith('https://')) {
    return 'https://' + link;
  }
  return link;
};

const Home = () => {
  const [homeData, setHomeData] = useState({
    stats: { families: 0, amenities: 0 },
    amenities: [],
    notices: [],
    events: [],
    gallery: [],
    businesses: []
  });
  const [dashboardLink, setDashboardLink] = useState("/login");

  const [isPromoModalOpen, setIsPromoModalOpen] = useState(false);
  const [isSubmittingPromo, setIsSubmittingPromo] = useState(false);
  const [promoForm, setPromoForm] = useState({ name: '', phone: '', businessName: '', description: '' });

  const getImageUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    const normalized = path.replace(/\\/g, '/');
    const baseUrl = import.meta.env.VITE_API_URL.replace(/\/api\/?$/, '');
    if (normalized.startsWith('/uploads')) {
        return `${baseUrl}/api${normalized}`;
    }
    return `${baseUrl}${normalized.startsWith('/') ? '' : '/'}${normalized}`;
  };

  useEffect(() => {
    const fetchHomeData = async () => {
      try {
        const { data } = await api.get('/public/home');
        setHomeData(data);
      } catch (error) {
        console.error("Failed to fetch home data", error);
      }
    };
    fetchHomeData();

    // Check login state for Resident Portal button
    const token = localStorage.getItem("token");
    const userInfoString = localStorage.getItem("userInfo");

    if (token && userInfoString) {
      try {
        const userInfo = JSON.parse(userInfoString);
        if (userInfo.role === 'superadmin') setDashboardLink("/superadmin");
        else if (userInfo.role === 'admin') setDashboardLink("/admin");
        else if (userInfo.role === 'security') setDashboardLink("/security");
        else setDashboardLink("/resident");
      } catch (e) {
        setDashboardLink("/login");
      }
    }

    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add(styles.inView);
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    const elements = document.querySelectorAll(`.${styles.animateOnScroll}`);
    elements.forEach(el => observer.observe(el));

    return () => {
      elements.forEach(el => observer.unobserve(el));
    };
  }, []);

  const handlePromoSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingPromo(true);
    try {
      await api.post('/public/business-request', {
        name: promoForm.name,
        phone: promoForm.phone,
        businessName: promoForm.businessName,
        description: promoForm.description
      });
      toast.success('Your request has been submitted successfully. Admin will contact you soon.');
      setIsPromoModalOpen(false);
      setPromoForm({ name: '', phone: '', businessName: '', description: '' });
    } catch (error) {
      console.error("Failed to submit request", error);
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setIsSubmittingPromo(false);
    }
  };

  return (
    <div className={styles.homeContainer}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroOverlay}></div>
        <div className={styles.heroBackground}>
          <img src="/images/hero-bg.webp" alt="Hero Background" fetchPriority="high" />
        </div>
        <div className={`${styles.heroContent} ${styles.animateOnScroll}`}>
          <div className={styles.heroBadge}>
            <Sparkles size={16} className={styles.goldIcon} />
            <span>WELCOME TO PREMIUM LIVING</span>
          </div>
          <h1 className={styles.heroTitle}>
            Sanskruti <br /> Shubh Aangan
          </h1>
          <p className={styles.heroDescription}>
            Experience a symphony of luxury, nature, and modern community living.
            Your sanctuary of peace in the heart of the city.
          </p>
          <div className={styles.heroActions}>
            <Button to="/about" className={styles.btnPrimary}>
              Explore Society <ArrowRight size={18} />
            </Button>
            <Button to={dashboardLink} className={styles.btnGlass}>
              Resident Portal
            </Button>
          </div>
        </div>

        {/* Floating Quick Stats */}
        <div className={styles.floatingStats}>
          <div className={styles.floatingStat}>
            <h4>{homeData.stats.families}+</h4>
            <p>Happy Families</p>
          </div>
          <div className={styles.floatingStat}>
            <h4>{homeData.stats.amenities}+</h4>
            <p>Premium Amenities</p>
          </div>
          <div className={styles.floatingStat}>
            <h4>24/7</h4>
            <p>Security & Support</p>
          </div>
        </div>
      </section>

      {/* Community Updates Section */}
      <section className={`${styles.updatesSection} ${styles.animateOnScroll}`}>
        <div className={styles.businessPromoFinalBanner}>
          <div className={styles.finalPromoHeader}>
            <div className={styles.finalEyebrowGroup}>
              <div className={styles.finalTitleDivider}></div>
              <span className={styles.finalEyebrowText}>SUPPORT OUR COMMUNITY</span>
              <div className={styles.finalTitleDivider}></div>
            </div>
            <h2 className={styles.finalBannerTitle}>
              Local Businesses, <span className={styles.textGold}>Stronger Community</span>
            </h2>
            <p className={styles.finalBannerSubtitle}>Discover and support trusted businesses from our community</p>
          </div>
          
          <div className={styles.finalPromoSliderWrapper}>
            <div className={styles.finalPromoSlider}>
              {homeData.businesses && homeData.businesses.length > 0 ? (
                homeData.businesses.length <= 6 ? (
                  <div className={styles.staticTrack}>
                    {homeData.businesses.map((business, idx) => {
                      const bizLink = getBusinessLink(business);
                      const CardComponent = bizLink ? 'a' : 'div';
                      const cardProps = bizLink ? { href: bizLink, target: "_blank", rel: "noopener noreferrer" } : {};
                      
                      return (
                        <CardComponent 
                          key={idx} 
                          className={styles.finalPromoCard} 
                          {...cardProps} 
                          style={{ textDecoration: 'none', cursor: bizLink ? 'pointer' : 'default', color: 'inherit' }}
                        >
                          <div className={styles.finalPromoImgBox}>
                            <img 
                              src={getImageUrl(business.logo || business.image) || "/images/placeholder-business.webp"} 
                              alt={business.name} 
                              className={styles.finalPromoImg} 
                              loading="lazy"
                            />
                          </div>
                          <div className={styles.finalPromoContent}>
                            <h4>{business.name}</h4>
                            <p>{business.category || "Business"}</p>
                          </div>
                        </CardComponent>
                      );
                    })}
                  </div>
                ) : (
                  <div className={styles.marqueeTrack}>
                    {[...homeData.businesses, ...homeData.businesses].map((business, idx) => {
                      const bizLink = getBusinessLink(business);
                      const CardComponent = bizLink ? 'a' : 'div';
                      const cardProps = bizLink ? { href: bizLink, target: "_blank", rel: "noopener noreferrer" } : {};
                      
                      return (
                        <CardComponent 
                          key={`mq-${idx}`} 
                          className={styles.finalPromoCard} 
                          {...cardProps} 
                          style={{ textDecoration: 'none', cursor: bizLink ? 'pointer' : 'default', color: 'inherit' }}
                        >
                          <div className={styles.finalPromoImgBox}>
                            <img 
                              src={getImageUrl(business.logo || business.image) || "/images/placeholder-business.webp"} 
                              alt={business.name} 
                              className={styles.finalPromoImg} 
                              loading="lazy"
                            />
                          </div>
                          <div className={styles.finalPromoContent}>
                            <h4>{business.name}</h4>
                            <p>{business.category || "Business"}</p>
                          </div>
                        </CardComponent>
                      );
                    })}
                  </div>
                )
              ) : (
                <div style={{ textAlign: 'center', color: '#888', flex: 1, padding: '1rem' }}>
                  <p>No businesses featured right now.</p>
                </div>
              )}
            </div>
          </div>

          <div className={styles.finalFeaturesBar}>
            <div className={styles.featureItem}>
              <div className={styles.featureIconWrap}><ShieldCheck size={16} /></div>
              <span>Trusted Businesses</span>
            </div>
            <div className={styles.featureDivider}></div>
            <div className={styles.featureItem}>
              <div className={styles.featureIconWrap}><Users size={16} /></div>
              <span>Community Support</span>
            </div>
            <div className={styles.featureDivider}></div>
            <div className={styles.featureItem}>
              <div className={styles.featureIconWrap}><TrendingUp size={16} /></div>
              <span>Grow Together</span>
            </div>
          </div>

          <div className={styles.finalCtaFooter}>
            <p>Want to promote your business?</p>
            <button className={styles.btnContactOutlineGold} onClick={() => setIsPromoModalOpen(true)}>
              Contact Now <ArrowRight size={16}/>
            </button>
          </div>
        </div>

                <div className={styles.updatesContainerTwoCol}>
          <div className={styles.updateColumnBox}>
            <div className={styles.columnHeaderRich}>
              <div className={styles.headerTitleArea}>
                <div className={styles.headerIconWrapperGreen}>
                  <Megaphone size={24} className={styles.iconGreen} />
                </div>
                <div>
                  <h3>Latest Notices</h3>
                  <p>Stay updated with important announcements</p>
                </div>
              </div>
              <Link to="/notices" className={styles.linkViewAllText}>VIEW ALL <ArrowRight size={14}/></Link>
            </div>
            
            <div className={styles.noticeList}>
              {homeData.notices.length > 0 ? (
                homeData.notices.slice(0, 2).map((notice, idx) => {
                  const dateStr = notice.date || notice.createdAt;
                  const dateObj = new Date(dateStr);
                  return (
                    <div key={idx} className={styles.noticeItem}>
                      <div className={styles.noticeMain}>
                        <div className={styles.dateBadge}>
                          <span className={styles.day}>{!isNaN(dateObj.getTime()) ? dateObj.getDate().toString().padStart(2, '0') : '-'}</span>
                          <span className={styles.month}>{!isNaN(dateObj.getTime()) ? dateObj.toLocaleString('default', { month: 'short' }).toUpperCase() : ''}</span>
                        </div>
                        <div className={styles.noticeContent}>
                          <h4>{notice.title}</h4>
                          <p className={styles.noticeDesc}>{notice.description || notice.content}</p>
                          {(notice.meetingDateTime || notice.meetingLocation) && (
                            <div className={styles.noticeMeta}>
                              {notice.meetingDateTime && (
                                <span className={styles.metaItem}>
                                  <Clock size={14} /> {notice.meetingDateTime}
                                </span>
                              )}
                              {notice.meetingLocation && (
                                <span className={styles.metaItem}>
                                  <MapPin size={14} /> {notice.meetingLocation}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <ChevronRight size={18} className={styles.noticeChevron} />
                    </div>
                  );
                })
              ) : (
                <div style={{ padding: '20px', textAlign: 'center', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px dashed #cbd5e1' }}>
                  <Bell size={32} color="#94a3b8" style={{ marginBottom: '12px' }} />
                  <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>No latest notices available.</p>
                </div>
              )}
            </div>
          </div>

          <div className={styles.updateColumnBox}>
            <div className={styles.columnHeaderRich}>
              <div className={styles.headerTitleArea}>
                <div className={styles.headerIconWrapperRed}>
                  <CalendarDays size={24} className={styles.iconRed} />
                </div>
                <div>
                  <h3>Upcoming Events</h3>
                  <p>Be a part of our community events</p>
                </div>
              </div>
              <Link to="/events" className={styles.linkViewAllText}>VIEW ALL <ArrowRight size={14}/></Link>
            </div>
            
            <div className={styles.eventListWrapper}>
              {homeData.events.length > 0 ? (
                homeData.events.slice(0, 1).map((event, idx) => (
                  <div key={idx} className={styles.eventCardHorizontal}>
                    <div className={styles.eventImageWrap}>
                      <img src={getImageUrl(event.image) || "/images/event_diwali_1787663959949.webp"} alt={event.title} className={styles.eventImg} />
                      <div className={styles.eventDateOverlaySmall}>
                        <strong>{new Date(event.date).getDate().toString().padStart(2, '0')}</strong>
                        <span>{new Date(event.date).toLocaleString('default', { month: 'short' }).toUpperCase()}</span>
                      </div>
                    </div>
                    <div className={styles.eventDetailsRight}>
                      {/* Top Row */}
                      <div className={styles.eventHeaderRow}>
                        <div className={styles.eventCategoryPill}>
                          <Users size={14} /> {event.category || 'Cultural Event'}
                        </div>
                        <Link to="/events" className={styles.btnSolidRsvp}>RSVP Now <ArrowRight size={14} /></Link>
                      </div>
                      
                      <h3 className={styles.eventTitle}>{event.title}</h3>
                      
                      <div className={styles.eventMetaInlineList}>
                        <span><MapPin size={14} /> {event.location || 'TBA'}</span>
                        <div className={styles.metaDivider}></div>
                        <span><CalendarDays size={14} /> {new Date(event.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                        <div className={styles.metaDivider}></div>
                        <span><Clock size={14} /> {event.time || 'TBA'}</span>
                      </div>
                      
                      <p className={styles.eventDescriptionText}>{event.description}</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className={styles.eventCardMain} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', minHeight: '300px', backgroundColor: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: '16px' }}>
                  <CalendarDays size={48} color="#94a3b8" style={{ marginBottom: '16px' }} />
                  <h3 style={{ fontSize: '18px', color: '#475569', marginBottom: '8px' }}>No Upcoming Events</h3>
                  <p style={{ color: '#64748b', textAlign: 'center', margin: 0, fontSize: '14px' }}>There are no upcoming events scheduled at the moment. Please check back later.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section className={`${styles.aboutSection} ${styles.animateOnScroll}`}>
        <div className={styles.aboutImageGrid}>
          <img src="/images/about-bg.webp" alt="Society View" className={styles.aboutImgMain} loading="lazy" />
          <div className={styles.experienceBadge}>
            <h3>1+</h3>
            <p>Years of Excellence</p>
          </div>
        </div>
        <div className={styles.aboutTextContent}>
          <h5 className={styles.sectionEyebrow}>ABOUT SANSKRITI</h5>
          <h2 className={styles.sectionTitle}>Where Luxury Meets Harmony</h2>
          <div className={styles.titleDivider}></div>
          <p className={styles.sectionDescription}>
            Nestled in lush greenery, Sanskriti Shubh Aangan offers an unparalleled lifestyle.
            We believe in creating not just homes, but a vibrant community where every moment
            is celebrated and every resident feels a deep sense of belonging.
          </p>
          <ul className={styles.aboutFeatures}>
            <li><ShieldCheck size={20} className={styles.goldIcon} /> State-of-the-art security systems</li>
            <li><Trees size={20} className={styles.goldIcon} /> Landscaped gardens & open spaces</li>
            <li><Building size={20} className={styles.goldIcon} /> Modern architectural design</li>
          </ul>
          <Link to="/about" className={styles.btnOutline}>
            Discover Our Story <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Amenities Section */}
      <section className={`${styles.amenitiesSection} ${styles.animateOnScroll}`}>
        <div className={styles.amenitiesHeader}>
          <div>
            <h5 className={styles.sectionEyebrow}>LIFESTYLE</h5>
            <h2 className={styles.sectionTitle}>World-Class Amenities</h2>
          </div>
          <Link to="/amenities" className={styles.linkWithIcon}>
            View All Amenities <ArrowRight size={18} />
          </Link>
        </div>

        {homeData.amenities.length > 0 ? (
          <div className={styles.amenitiesGrid}>
            {homeData.amenities.slice(0, 4).map((amenity, idx) => (
              <div key={idx} className={styles.amenityCard}>
                <div className={styles.amenityIconWrapper}>
                  <Sparkles size={28} className={styles.amenityIcon} />
                </div>
                <h3>{amenity.name}</h3>
                <p>{amenity.description || "Premium amenity for residents."}</p>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b', background: '#fff', borderRadius: '12px', border: '1px dashed #cbd5e1' }}>
            <p style={{ fontSize: '1.1rem', margin: 0 }}>Amenities will be updated soon.</p>
          </div>
        )}
      </section>


      {/* Gallery Section */}
      <section className={`${styles.gallerySection} ${styles.animateOnScroll}`}>
        <div className={styles.galleryHeader}>
          <div>
            <h5 className={styles.sectionEyebrow}>GALLERY</h5>
            <h2 className={styles.sectionTitle}>Life at Sanskruti</h2>
          </div>
          <Link to="/gallery" className={styles.btnOutline}>
            <Camera size={18} /> View Gallery
          </Link>
        </div>
        {homeData.gallery && homeData.gallery.length > 0 ? (
          <div className={styles.homeGalleryGrid}>
            {homeData.gallery.slice(0, 3).map((g, idx) => (
              <div key={idx} className={styles.homeGalleryItem}>
                <img src={getImageUrl(g.url)} alt={`Gallery ${idx + 1}`} loading="lazy" />
              </div>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b', background: '#fff', borderRadius: '12px', border: '1px dashed #cbd5e1' }}>
            <Camera size={48} color="#94a3b8" style={{ marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', color: '#475569', margin: '0 0 8px 0' }}>Gallery is Empty</h3>
            <p style={{ margin: 0, fontSize: '14px' }}>Images uploaded by the admin will appear here.</p>
          </div>
        )}
      </section>

      {/* Emergency Section */}
      <section className={`${styles.emergencySection} ${styles.animateOnScroll}`}>
        <h2 className={styles.emergencyTitle}>Emergency Contacts</h2>
        <div className={styles.emergencyGrid}>
          {[
            { title: "Main Gate Security", number: "+91 00000 00000", icon: ShieldAlert },
            { title: "Society Office", number: "+91 00000 00000", icon: Phone },
            { title: "Ambulance", number: "108", icon: Truck },
            { title: "Fire Station", number: "101", icon: Flame },
            { title: "Police Station", number: "112", icon: Siren },
          ].map((contact, idx) => {
            const Icon = contact.icon;
            return (
              <div key={idx} className={styles.emergencyCard}>
                <div className={styles.emergencyIconRing}>
                  <Icon size={24} />
                </div>
                <div className={styles.emergencyInfo}>
                  <h4>{contact.title}</h4>
                  <p>{contact.number}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Business Promotion Modal */}
      <Modal isOpen={isPromoModalOpen} onClose={() => setIsPromoModalOpen(false)} title="Promote Your Business">
        <form onSubmit={handlePromoSubmit} className={styles.promoForm}>
          <div className={styles.formGroup}>
            <label>Your Name *</label>
            <input type="text" required value={promoForm.name} onChange={e => setPromoForm({...promoForm, name: e.target.value})} className={styles.formInput} placeholder="Enter your full name" />
          </div>
          <div className={styles.formGroup}>
            <label>Contact Number *</label>
            <input type="tel" required value={promoForm.phone} onChange={e => setPromoForm({...promoForm, phone: e.target.value})} className={styles.formInput} placeholder="Enter your mobile number" />
          </div>
          <div className={styles.formGroup}>
            <label>Business Name *</label>
            <input type="text" required value={promoForm.businessName} onChange={e => setPromoForm({...promoForm, businessName: e.target.value})} className={styles.formInput} placeholder="Enter your business name" />
          </div>
          <div className={styles.formGroup}>
            <label>Business Description *</label>
            <textarea required rows="4" value={promoForm.description} onChange={e => setPromoForm({...promoForm, description: e.target.value})} className={styles.formInput} placeholder="Tell us about your products or services..."></textarea>
          </div>
          <button type="submit" disabled={isSubmittingPromo} className={styles.btnPrimary} style={{ width: '100%', marginTop: '1rem', borderRadius: '8px' }}>
            {isSubmittingPromo ? 'Submitting...' : 'Submit Request'}
          </button>
        </form>
      </Modal>

    </div>
  );
};

export default Home;
